<template>
    <div class="content_div">
        <el-card>
            <!-- 试卷信息头部 -->
            <div class="paper-header">
                <h2>{{ paperInfo.paperTitle || '单词测评试卷' }}</h2>
                <div class="paper-meta">
                    <span>学生：{{ paperInfo.studentName }}</span>
                    <span class="divider">|</span>
                    <span>总分：{{ paperInfo.totalScore }}</span>
                    <span class="divider">|</span>
                    <span>
                        剩余时间：
                        <el-tag :type="timeLeft < 300 ? 'danger' : 'success'">
                            {{ formatTime(timeLeft) }}
                        </el-tag>
                    </span>
                </div>
            </div>

            <el-divider></el-divider>

            <!-- 题目列表 -->
            <div class="questions-container">
                <el-card 
                    v-for="(question, index) in questions" 
                    :key="question.id"
                    shadow="hover"
                    class="question-card">
                    
                    <div slot="header" class="question-header">
                        <span class="question-number">第 {{ index + 1 }} 题</span>
                        <el-tag size="small" :type="getQuestionTypeColor(question.questionType)">
                            {{ getQuestionTypeName(question.questionType) }}
                        </el-tag>
                    </div>

                    <!-- 题目内容 -->
                    <div class="question-content">
                        <p class="question-text" v-html="question.questionContent"></p>
                        
                        <!-- 短语填空提示 -->
                        <p v-if="question.questionType === 3" class="hint-text">
                            完整短语：{{ question.extensionContentSnapshot }}
                        </p>
                        
                        <!-- 完形填空段落 -->
                        <div v-if="question.questionType === 4" class="cloze-passage">
                            <p v-html="question.extensionContentSnapshot"></p>
                        </div>
                        
                        <!-- 阅读理解短文 -->
                        <div v-if="question.questionType === 5" class="reading-passage">
                            <p v-html="question.extensionContentSnapshot"></p>
                            <el-divider></el-divider>
                        </div>
                    </div>

                    <!-- 答题区域 -->
                    <div class="answer-area">
                        <!-- 文本输入 -->
                        <el-input
                            v-if="[1, 2, 3].includes(question.questionType)"
                            v-model="question.studentAnswer"
                            placeholder="请输入你的答案"
                            @input="handleAnswerChange(question.id)">
                        </el-input>
                        
                        <!-- 选择题选项 -->
                        <el-radio-group 
                            v-if="question.questionType === 6 || question.questionType === 7"
                            v-model="question.studentAnswer"
                            @change="handleAnswerChange(question.id)">
                            <el-radio 
                                v-for="(option, optIndex) in parseOptions(question.options)"
                                :key="optIndex"
                                :label="option">
                                {{ option }}
                            </el-radio>
                        </el-radio-group>
                    </div>

                    <!-- 图片上传 -->
                    <div class="image-upload-area">
                        <p class="upload-label">上传答题图片（可选）：</p>
                        <el-upload
                            action="/api/upload"
                            list-type="picture-card"
                            :on-success="(res) => handleUploadSuccess(res, question.id)"
                            :on-remove="(file) => handleRemove(file, question.id)"
                            :file-list="question.imageList || []"
                            :limit="3">
                            <i class="el-icon-plus"></i>
                        </el-upload>
                    </div>
                </el-card>
            </div>

            <!-- 底部操作栏 -->
            <div class="action-bar">
                <el-button @click="saveDraft">保存草稿</el-button>
                <el-button type="primary" @click="submitPaper" :loading="submitting">
                    提交试卷
                </el-button>
            </div>
        </el-card>
    </div>
</template>

<script>
import { getPaperDetail, getPaperQuestions, submitPaper } from '@/api/education/assessment'

export default {
    name: 'AnswerPaper',
    
    data() {
        return {
            paperId: null,
            paperInfo: {},
            questions: [],
            timeLeft: 3600, // 默认60分钟
            timer: null,
            submitting: false
        }
    },
    
    created() {
        this.paperId = this.$route.params.paperId
        this.loadPaperInfo()
        this.loadQuestions()
        this.startTimer()
    },
    
    beforeDestroy() {
        if (this.timer) {
            clearInterval(this.timer)
        }
    },
    
    methods: {
        // 加载试卷信息
        async loadPaperInfo() {
            try {
                const res = await getPaperDetail(this.paperId)
                this.paperInfo = res.data
            } catch (error) {
                this.$message.error('加载试卷信息失败')
            }
        },
        
        // 加载题目列表
        async loadQuestions() {
            try {
                const res = await getPaperQuestions(this.paperId)
                let questions = res.data || []
                // 收集本次试卷所有单词，在短文内容中加粗展示
                const paperWords = [...new Set(questions.map(q => q.word).filter(Boolean))]
                if (paperWords.length > 0) {
                    questions = questions.map(q => {
                        if (q.questionContent) {
                            let text = q.questionContent
                            paperWords.forEach(w => {
                                const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                const regex = new RegExp('\\b(' + escaped + ')\\b', 'gi')
                                text = text.replace(regex, '<b>$1</b>')
                            })
                            q.questionContent = text
                        }
                        if (q.extensionContentSnapshot) {
                            let text = q.extensionContentSnapshot
                            paperWords.forEach(w => {
                                const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                                const regex = new RegExp('\\b(' + escaped + ')\\b', 'gi')
                                text = text.replace(regex, '<b>$1</b>')
                            })
                            q.extensionContentSnapshot = text
                        }
                        return q
                    })
                }
                this.questions = questions
            } catch (error) {
                this.$message.error('加载题目失败')
            }
        },
        
        // 开始计时器
        startTimer() {
            this.timer = setInterval(() => {
                this.timeLeft--
                
                if (this.timeLeft <= 0) {
                    clearInterval(this.timer)
                    this.$message.warning('考试时间已到，自动提交试卷')
                    this.submitPaper()
                }
            }, 1000)
        },
        
        // 格式化时间
        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60)
            const secs = seconds % 60
            return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        },
        
        // 答案变化
        handleAnswerChange(questionId) {
            // TODO: 可以添加自动保存逻辑
            console.log('答案变化:', questionId)
        },
        
        // 图片上传成功
        handleUploadSuccess(res, questionId) {
            const question = this.questions.find(q => q.id === questionId)
            if (question) {
                if (!question.imageList) {
                    question.imageList = []
                }
                question.imageList.push({
                    name: res.fileName,
                    url: res.url
                })
            }
        },
        
        // 移除图片
        handleRemove(file, questionId) {
            const question = this.questions.find(q => q.id === questionId)
            if (question && question.imageList) {
                const index = question.imageList.findIndex(img => img.url === file.url)
                if (index > -1) {
                    question.imageList.splice(index, 1)
                }
            }
        },
        
        // 解析选项
        parseOptions(optionsStr) {
            if (!optionsStr) return []
            try {
                return JSON.parse(optionsStr)
            } catch (e) {
                return []
            }
        },
        
        // 获取题型名称
        getQuestionTypeName(type) {
            const typeMap = {
                1: '英译中',
                2: '中译英',
                3: '短语填空',
                4: '完形填空',
                5: '阅读理解',
                6: '同义词选择',
                7: '反义词选择'
            }
            return typeMap[type] || '未知'
        },
        
        // 获取题型颜色
        getQuestionTypeColor(type) {
            const colorMap = {
                1: '',
                2: 'success',
                3: 'warning',
                4: 'danger',
                5: 'info',
                6: '',
                7: ''
            }
            return colorMap[type] || ''
        },
        
        // 保存草稿
        saveDraft() {
            this.$message.success('草稿已保存')
            // TODO: 实现草稿保存逻辑
        },
        
        // 提交试卷
        submitPaper() {
            this.$confirm('确定要提交试卷吗？提交后将无法修改答案。', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                this.submitting = true
                
                try {
                    // 构建答案数据
                    const answerData = this.questions.map(q => ({
                        questionId: q.id,
                        studentAnswer: q.studentAnswer,
                        imageList: q.imageList || []
                    }))
                    
                    await submitPaper(this.paperId, JSON.stringify(answerData))
                    
                    this.$message.success('试卷提交成功')
                    
                    // 跳转到查看页面
                    setTimeout(() => {
                        this.$router.push('/education/exam/assessment')
                    }, 800)
                    
                } catch (error) {
                    this.$message.error('提交失败：' + (error.message || '未知错误'))
                } finally {
                    this.submitting = false
                }
            }).catch(() => {})
        }
    }
}
</script>

<style scoped lang="scss">
.paper-header {
    h2 {
        margin: 0 0 15px 0;
        font-size: 24px;
        color: #303133;
    }
    
    .paper-meta {
        font-size: 14px;
        color: #606266;
        
        .divider {
            margin: 0 10px;
            color: #dcdfe6;
        }
    }
}

.questions-container {
    .question-card {
        margin-bottom: 20px;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
    
    .question-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .question-number {
            font-weight: bold;
            font-size: 16px;
            color: #303133;
        }
    }
    
    .question-content {
        margin-bottom: 20px;
        
        .question-text {
            font-size: 16px;
            color: #303133;
            line-height: 1.6;
        }
        
        .hint-text {
            margin-top: 10px;
            font-size: 14px;
            color: #909399;
        }
        
        .cloze-passage,
        .reading-passage {
            padding: 15px;
            background: #f5f7fa;
            border-radius: 4px;
            line-height: 1.8;
        }
    }
    
    .answer-area {
        margin-top: 20px;
    }
    
    .image-upload-area {
        margin-top: 20px;
        
        .upload-label {
            margin-bottom: 10px;
            font-size: 14px;
            color: #606266;
        }
    }
}

.action-bar {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #ebeef5;
    text-align: center;
    
    .el-button {
        min-width: 120px;
        margin: 0 10px;
    }
}
</style>
